import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { existsSync, readFileSync } from "node:fs";
import { resolve } from "node:path";
import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { z } from "zod/v3";
import { kernelQuery, kernelMutation, getAgentSessionId } from "../client.js";
import { success, successResult, failureResult, type ToolResult } from "../envelope.js";
import { resolveProjectRoot } from "../lib/resolve-project-root.js";

// ── Types ──────────────────────────────────────────────────────────────

type MappingKind = "file" | "schema" | "conceptual";
type CheckResult = "verified" | "drifted" | "unverifiable";

interface MappingCheck {
  entryId: string;
  entryName: string;
  field: string;
  kind: MappingKind;
  result: CheckResult;
  reason: string;
  currentStatus: string;
}

interface RefCheck {
  entryId: string;
  entryName: string;
  refValue: string;
  found: boolean;
}

// ── Schema Parser ──────────────────────────────────────────────────────

function parseConvexSchema(schemaPath: string): Map<string, Set<string>> {
  const content = readFileSync(schemaPath, "utf-8");
  const tables = new Map<string, Set<string>>();
  let currentTable: string | null = null;

  for (const line of content.split("\n")) {
    const tableMatch = line.match(/^\t(\w+):\s*defineTable\(/);
    if (tableMatch) {
      currentTable = tableMatch[1];
      tables.set(currentTable, new Set());
      continue;
    }

    if (currentTable && /^\t[}\)]/.test(line) && !/^\t\t/.test(line)) {
      currentTable = null;
      continue;
    }

    if (currentTable) {
      const fieldMatch = line.match(/^\t\t(\w+):\s*v\./);
      if (fieldMatch) tables.get(currentTable)!.add(fieldMatch[1]);
    }
  }

  return tables;
}

// ── Code Mapping Classifiers ───────────────────────────────────────────

function cleanFieldRef(field: string): string {
  return field
    .replace(/\s*\(.*\)\s*$/, "")
    .replace(/\s*=\s*.*$/, "")
    .trim();
}

function splitMultiRefs(field: string): string[] {
  if (field.includes(" + ")) return field.split(/\s*\+\s*/);
  return [field];
}

const FILE_EXT_RE = /\.(ts|js|svelte|json|css|md|jsx|tsx|mjs|cjs)(?:\s|$)/i;

function classifyRef(cleaned: string): MappingKind {
  if (cleaned.includes("/") || FILE_EXT_RE.test(cleaned) || /^\.\w+/.test(cleaned)) {
    return "file";
  }
  if (/^\w+\.\w+$/.test(cleaned)) return "schema";
  if (/^\w+\s+table$/i.test(cleaned)) return "schema";
  return "conceptual";
}

// ── Verification Checkers ──────────────────────────────────────────────

function checkFileRef(ref: string, root: string): { result: CheckResult; reason: string } {
  const filePart = ref.split(/\s+/)[0];
  const fullPath = resolve(root, filePart);
  if (existsSync(fullPath)) return { result: "verified", reason: "exists" };
  return { result: "drifted", reason: `not found: ${filePart}` };
}

function checkSchemaRef(
  ref: string,
  schema: Map<string, Set<string>>,
): { result: CheckResult; reason: string } {
  const tableOnlyMatch = ref.match(/^(\w+)\s+table$/i);
  if (tableOnlyMatch) {
    const table = tableOnlyMatch[1];
    if (schema.has(table)) return { result: "verified", reason: `table "${table}" exists` };
    return { result: "drifted", reason: `table "${table}" not found in schema` };
  }

  const dotIdx = ref.indexOf(".");
  if (dotIdx > 0) {
    const table = ref.slice(0, dotIdx);
    const field = ref.slice(dotIdx + 1);
    if (!schema.has(table)) return { result: "drifted", reason: `table "${table}" not found in schema` };
    if (!schema.get(table)!.has(field)) return { result: "drifted", reason: `field "${field}" not found in table "${table}"` };
    return { result: "verified", reason: `${table}.${field} exists` };
  }

  return { result: "unverifiable", reason: "could not parse schema reference" };
}

// ── Trust Report Formatter ─────────────────────────────────────────────

function formatTrustReport(
  collection: string,
  entryCount: number,
  mappings: MappingCheck[],
  refs: RefCheck[],
  fixes: string[],
  mode: string,
  schemaTableCount: number,
  projectRoot: string,
): string {
  const verified = mappings.filter((c) => c.result === "verified").length;
  const drifted = mappings.filter((c) => c.result === "drifted").length;
  const unverifiable = mappings.filter((c) => c.result === "unverifiable").length;

  const refsValid = refs.filter((c) => c.found).length;
  const refsBroken = refs.filter((c) => !c.found).length;

  const totalChecks = mappings.length + refs.length;
  const totalPassed = verified + refsValid;
  const trustScore = totalChecks > 0 ? Math.round((totalPassed / totalChecks) * 100) : 100;

  const lines: string[] = [
    `# Trust Report: ${collection} (${entryCount} entries scanned)`,
    "",
  ];

  if (mappings.length > 0) {
    lines.push(
      "## Code Mapping Verification",
      `- ${mappings.length} mappings checked`,
      `- ${verified} verified (${Math.round((verified / mappings.length) * 100)}%)`,
      `- ${drifted} drifted (file/schema not found)`,
      `- ${unverifiable} unverifiable (conceptual — skipped)`,
      "",
    );
  }

  if (drifted > 0) {
    lines.push("### Drifted Mappings");
    for (const mc of mappings.filter((c) => c.result === "drifted")) {
      lines.push(`- **${mc.entryId}** (${mc.entryName}): \`${mc.field}\` — ${mc.reason}`);
    }
    lines.push("");
  }

  if (unverifiable > 0) {
    lines.push("### Unverifiable (Conceptual)");
    for (const mc of mappings.filter((c) => c.result === "unverifiable")) {
      lines.push(`- **${mc.entryId}** (${mc.entryName}): \`${mc.field}\``);
    }
    lines.push("");
  }

  if (refs.length > 0) {
    lines.push(
      "## Cross-Reference Verification",
      `- ${refs.length} references checked`,
      `- ${refsValid} valid (${refs.length > 0 ? Math.round((refsValid / refs.length) * 100) : 0}%)`,
      `- ${refsBroken} broken`,
      "",
    );
  }

  if (refsBroken > 0) {
    lines.push("### Broken References");
    for (const rc of refs.filter((c) => !c.found)) {
      lines.push(`- **${rc.entryId}** (${rc.entryName}): relatedRules \`${rc.refValue}\` — entry not found`);
    }
    lines.push("");
  }

  lines.push(`## Trust Score: ${trustScore}% (${totalPassed} of ${totalChecks} checks passed)`);

  if (mode === "fix" && fixes.length > 0) {
    lines.push(
      "",
      "## Applied Fixes",
      `Updated codeMapping status from \`aligned\` → \`drifted\` on ${fixes.length} entr${fixes.length === 1 ? "y" : "ies"}:`,
    );
    for (const eid of fixes) lines.push(`- ${eid}`);
  } else if (mode === "report" && drifted > 0) {
    const fixable = mappings.filter((c) => c.result === "drifted" && c.currentStatus === "aligned").length;
    if (fixable > 0) {
      lines.push("", `_${fixable} mapping(s) marked "aligned" but actually drifted. Run with mode="fix" to update._`);
    }
  }

  lines.push("", "---", `_Schema: ${schemaTableCount} tables parsed from convex/schema.ts. Project root: ${projectRoot}_`);

  return lines.join("\n");
}

// ── Tool Registration ──────────────────────────────────────────────────

export const verifySchema = z.object({
  collection: z.string().max(200).default("glossary")
    .describe("Collection slug to verify (default: glossary)"),
  mode: z.enum(["report", "fix"]).default("report")
    .describe("'report' = read-only trust report. 'fix' = also update drifted codeMapping statuses."),
});

export const verifyEntrySchema = z.object({
  entryId: z.string().max(200).describe("Human entry ID (e.g. '<PREFIX>-<n>') to mark as verified"),
});

/**
 * WP-484 S2 (§3): extracted verify-chain handler — reused by the legacy
 * `verify` standalone tool AND `quality action=verify-chain` (absorbed per
 * §3 core-nouns table). One business-logic path.
 */
export async function handleVerifyChain(
  server: McpServer,
  { collection, mode }: { collection: string; mode: "report" | "fix" },
) {
      const projectRoot = resolveProjectRoot();
      if (!projectRoot) {
        return failureResult(
          "# Verification Failed\n\n" +
            "Cannot find project root (looked for `convex/schema.ts` in cwd and parent directory).\n\n" +
            "Set `WORKSPACE_PATH` in `.env.mcp` to the absolute path of the Product Brain project root.",
          "VALIDATION_ERROR",
          "Cannot find project root. Set WORKSPACE_PATH in .env.mcp.",
        );
      }

      const schema = parseConvexSchema(resolve(projectRoot, "convex/schema.ts"));

      await server.sendLoggingMessage({
        level: "info",
        data: `Verifying "${collection}" against ${schema.size} schema tables at ${projectRoot}`,
        logger: "product-os",
      });

      const scopedEntries = await kernelQuery<GatewayRouteReturnByName<"chain.listEntries">>("chain.listEntries", { collectionSlug: collection });

      if (scopedEntries.length === 0) {
        return failureResult(
          `No entries found in '${collection}'. Nothing to verify.`,
          "NOT_FOUND",
          `No entries found in '${collection}'. Nothing to verify.`,
          "Capture entries first, then verify.",
          [{ tool: "capture", description: "Capture an entry", parameters: {} }],
        );
      }

      await server.sendLoggingMessage({
        level: "info",
        data: `Scanning ${scopedEntries.length} entries in ${collection}...`,
        logger: "product-brain",
      });

      // Build cross-reference lookup: fetch all entries to check relatedRules
      let allEntryIds: Set<string>;
      try {
        const allEntries = await kernelQuery<GatewayRouteReturnByName<"chain.listEntries">>(
          "chain.listEntries",
          { fields: ["entryId"] },
        );
        allEntryIds = new Set((allEntries ?? []).map((e) => e.entryId).filter(Boolean) as string[]);
      } catch {
        allEntryIds = new Set(scopedEntries.map((e: any) => e.entryId).filter(Boolean));
      }

      const mappingChecks: MappingCheck[] = [];
      const refChecks: RefCheck[] = [];

      for (let idx = 0; idx < scopedEntries.length; idx++) {
        const entry = scopedEntries[idx];

        if (idx > 0 && idx % 10 === 0) {
          await server.sendLoggingMessage({
            level: "info",
            data: `Scanned ${idx}/${scopedEntries.length} entries...`,
            logger: "product-brain",
          });
        }

        // `chain.listEntries`'s handler infers a union return type (raw `EntryDoc[]` when no
        // `fields` projection is requested, vs `Record<string, unknown>[]` when it is) —
        // property access resolves to `unknown` across that union even though this call site
        // never passes `fields` (always the `EntryDoc[]` branch at runtime). Same documented
        // residual as packages/mcp-server/src/prompts/index.ts (PR #395, Lane C S3b pass).
        const eid = (entry.entryId as string | undefined) ?? (entry.name as string);
        const ename = entry.name as string;

        // ── Code mapping verification ──
        const codeMappings: any[] = entry.data?.codeMapping ?? [];
        for (const cm of codeMappings) {
          const rawField: string = cm.field ?? "";
          for (const rawRef of splitMultiRefs(rawField)) {
            const cleaned = cleanFieldRef(rawRef);
            if (!cleaned) continue;

            const kind = classifyRef(cleaned);
            let result: CheckResult;
            let reason: string;

            if (kind === "file") {
              ({ result, reason } = checkFileRef(cleaned, projectRoot));
            } else if (kind === "schema") {
              ({ result, reason } = checkSchemaRef(cleaned, schema));
            } else {
              result = "unverifiable";
              reason = "conceptual reference";
            }

            mappingChecks.push({
              entryId: eid, entryName: ename, field: rawRef.trim(),
              kind, result, reason, currentStatus: cm.status ?? "unknown",
            });
          }
        }

        // ── Cross-reference verification (relatedRules only) ──
        const MAX_CROSS_REFS = 50;
        const relatedRules: string[] = entry.data?.relatedRules ?? [];
        for (const ruleId of relatedRules) {
          if (refChecks.length >= MAX_CROSS_REFS) break;
          refChecks.push({
            entryId: eid, entryName: ename, refValue: ruleId,
            found: allEntryIds.has(ruleId),
          });
        }
      }

      await server.sendLoggingMessage({
        level: "info",
        data: `Scan complete. Building trust report...`,
        logger: "product-brain",
      });

      // ── Fix mode: update aligned → drifted ──
      const fixes: string[] = [];
      const fixUpdateWarnings: Array<{ entryId: string; normalizationWarnings: string[]; validationWarnings: string[] }> = [];
      // WP-465 slice ⑤ (Option B): coherency-refused fixes — the drift update did NOT apply,
      // so they are surfaced separately and never counted as fixed.
      const fixRefusals: Array<{ entryId: string; reason: string }> = [];
      if (mode === "fix") {
        const driftedByEntry = new Map<string, Set<string>>();
        for (const mc of mappingChecks) {
          if (mc.result === "drifted" && mc.currentStatus === "aligned") {
            if (!driftedByEntry.has(mc.entryId)) driftedByEntry.set(mc.entryId, new Set());
            driftedByEntry.get(mc.entryId)!.add(mc.field);
          }
        }

        for (const [eid, driftedFields] of driftedByEntry) {
          const entry = scopedEntries.find((e: any) => (e.entryId ?? e.name) === eid);
          if (!entry?.entryId) continue;
          // Same `chain.listEntries` union residual as above — `entryId` is provably a
          // truthy string past the guard, but the type stays `unknown` across the union.
          const targetEntryId = entry.entryId as string;

          const updated = (entry.data?.codeMapping ?? []).map((cm: any) =>
            cm.status === "aligned" && driftedFields.has(cm.field)
              ? { ...cm, status: "drifted" }
              : cm,
          );

          const updateResult = await kernelMutation<GatewayRouteReturnByName<"chain.updateEntry">>("chain.updateEntry", {
            entryId: targetEntryId,
            data: { codeMapping: updated },
          });
          // WP-465 slice ⑤ (Option B): a coherency refusal RETURNS a structured payload — the
          // drift fix did NOT apply, so surface it instead of counting it as fixed.
          if (updateResult.outcome === "refused") {
            fixRefusals.push({
              entryId: targetEntryId,
              reason: updateResult.refusal?.reason ?? "coherency gate refused this update",
            });
            continue;
          }
          if (updateResult.normalizationWarnings.length > 0 || updateResult.validationWarnings.length > 0) {
            fixUpdateWarnings.push({
              entryId: targetEntryId,
              normalizationWarnings: updateResult.normalizationWarnings,
              validationWarnings: updateResult.validationWarnings,
            });
          }
          fixes.push(targetEntryId);
        }
      }

      const verified = mappingChecks.filter((c) => c.result === "verified").length;
      const drifted = mappingChecks.filter((c) => c.result === "drifted").length;
      const unverifiable = mappingChecks.filter((c) => c.result === "unverifiable").length;
      const refsValid = refChecks.filter((c) => c.found).length;
      const refsBroken = refChecks.filter((c) => !c.found).length;
      const totalChecks = mappingChecks.length + refChecks.length;
      const totalPassed = verified + refsValid;
      const trustScore = totalChecks > 0 ? Math.round((totalPassed / totalChecks) * 100) : 100;

      const report = formatTrustReport(
        collection, scopedEntries.length,
        mappingChecks, refChecks, fixes, mode,
        schema.size, projectRoot,
      );

      let reportText = report;
      if (fixRefusals.length > 0) {
        reportText += '\n\n---\n\n🛑 **Coherency refusals (fix NOT applied — entry unchanged):**';
        for (const r of fixRefusals) {
          reportText += `\n  - **${r.entryId}**: ${r.reason}`;
        }
      }
      if (fixUpdateWarnings.length > 0) {
        reportText += '\n\n---\n\n⚠️ **Fix update warnings:**';
        for (const w of fixUpdateWarnings) {
          for (const nw of w.normalizationWarnings) {
            reportText += `\n  - **${w.entryId}** (normalization): ${nw}`;
          }
          for (const vw of w.validationWarnings) {
            reportText += `\n  - **${w.entryId}** (validation): ${vw}`;
          }
        }
      }

      return {
        content: [{ type: "text" as const, text: reportText }],
        structuredContent: success(
          `Trust report for ${collection}: ${totalPassed}/${totalChecks} checks passed (${trustScore}%).`,
          {
            collection,
            entryCount: scopedEntries.length,
            trustScore,
            totalChecks,
            totalPassed,
            verified,
            drifted,
            unverifiable,
            refsValid,
            refsBroken,
            fixesApplied: fixes.length,
            ...(fixRefusals.length > 0 ? { fixesRefused: fixRefusals.length, coherencyRefusals: fixRefusals } : {}),
            mode,
          },
        ),
      };
}

// WP-484 S3 (clean break, TEN-2157): the standalone `verify` / `verify-entry`
// tool registrations retired here. handleVerifyChain / handleVerifyEntry stay
// live — quality.ts (action=verify-chain) and entries.ts (action=verify) are
// their only remaining callers.

/**
 * WP-484 S1 (§3/§4 entries-family migration): verify-entry's handler body,
 * extracted so both `verify-entry` and `entries action="verify"` share it.
 */
export async function handleVerifyEntry({ entryId }: z.infer<typeof verifyEntrySchema>): Promise<ToolResult> {
  const agentSessionId = getAgentSessionId();
  const result = await kernelMutation<GatewayRouteReturnByName<"chain.verifyEntry">>(
    "chain.verifyEntry",
    {
      entryId,
      ...(agentSessionId ? { sessionId: agentSessionId } : {}),
    },
  );
  // WP-A T8: honest label when the server ships verifiedBy — connectors never re-derive
  // (spec §5). Old server omits the field, so keep today's bare text (spec §11 rollout rule).
  // Final-review Fix 3: a noop re-verify must say "already verified" — the mutation
  // performed no fresh attestation, so claiming "verified" is a lie of omission.
  //
  // Attestation-model finding (PR #341 review): a non-'attested' basis means
  // verifiedBy is a QUALIFIER, not a person's name (e.g. a legacy row's
  // 'legacy — unclassified') — rendering it as "— by <qualifier>" produces the
  // nonsense "is already verified — by legacy — unclassified". Branch on
  // attestation.basis the same way packages/mcp-server/src/tools/entries.ts's
  // renderVerificationLine does: attested → "— by <name> (<strength>)"; any
  // other basis → bare qualifier "(<value>)". Never re-derive strength here.
  const basis = result.attestation?.basis;
  const strength = result.attestation?.strength;
  const verifiedBySuffix = result.verifiedBy
    ? basis && basis !== "attested"
      ? ` (${result.verifiedBy})`
      : ` — by ${result.verifiedBy}${strength ? ` (${strength})` : ""}`
    : "";
  const message = result.alreadyVerified
    ? `Entry ${entryId} is already verified${verifiedBySuffix}.`
    : `Entry ${entryId} verified${verifiedBySuffix}.`;
  // successResult wraps the envelope into a ToolResult ({ content,
  // structuredContent }); bare success() omits structuredContent, which
  // makes thinWrapper throw "migration incomplete" AFTER the mutation has
  // already persisted — a gate that lies about failing (TEN-2307).
  return successResult(message, message, result);
}
